import { useEffect, useRef, useState } from "react";
import type { Video } from "@/types/video";

interface VideoPlayerProps {
  video: Video;
}

function formatTime(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function VideoPlayer({ video }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 2;
        if (next >= video.duration) {
          setPlaying(false);
          return video.duration;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [playing, video.duration]);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = async () => {
    const node = containerRef.current;
    if (!node) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await node.requestFullscreen();
      }
    } catch {
      // fullscreen may be blocked in some environments
    }
  };

  const progress = video.duration > 0 ? (elapsed / video.duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
    >
      <div className="relative aspect-video w-full">
        <img
          src={video.cover}
          alt={`${video.title} 视频封面`}
          title={`${video.title} - 啪啪视频`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/30"></div>

        <button
          type="button"
          onClick={() => setPlaying((prev) => !prev)}
          aria-label={playing ? "暂停演示播放" : "开始演示播放"}
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className={`flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-all duration-300 hover:bg-primary-600 md:h-20 md:w-20 ${
              playing ? "opacity-0 hover:opacity-100" : "opacity-100"
            }`}
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-4xl`}></i>
          </span>
        </button>

        {playing ? (
          <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-md bg-black/55 px-3 py-1.5 backdrop-blur">
            <span className="flex items-end gap-0.5">
              <span className="h-2 w-0.5 animate-soft-pulse rounded-full bg-accent-400"></span>
              <span className="h-3 w-0.5 animate-soft-pulse rounded-full bg-accent-400 [animation-delay:0.2s]"></span>
              <span className="h-2.5 w-0.5 animate-soft-pulse rounded-full bg-accent-400 [animation-delay:0.4s]"></span>
            </span>
            <span className="text-xs text-white">演示播放中</span>
          </div>
        ) : null}

        {playing ? (
          <div className="pointer-events-none absolute bottom-16 left-1/2 w-[90%] max-w-xl -translate-x-1/2 rounded-md bg-black/60 px-3 py-2 text-center text-xs text-white/90 backdrop-blur">
            <i className="ri-information-line mr-1"></i>
            本播放器为演示模式，仅展示静态封面与播放动画，暂不含真实视频流。
          </div>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
          <button
            type="button"
            onClick={() => setPlaying((prev) => !prev)}
            aria-label={playing ? "暂停" : "播放"}
            className="cursor-pointer text-white transition-colors duration-200 hover:text-primary-400"
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-2xl`}></i>
          </button>
          <button
            type="button"
            onClick={() => setMuted((prev) => !prev)}
            aria-label={muted ? "取消静音" : "静音"}
            className="cursor-pointer text-white transition-colors duration-200 hover:text-primary-400"
          >
            <i className={`${muted ? "ri-volume-mute-line" : "ri-volume-up-line"} text-xl`}></i>
          </button>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/25">
            <div className="h-full rounded-full bg-primary-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="font-label text-xs text-white/90">
            {formatTime(elapsed)} / {formatTime(video.duration)}
          </span>
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "退出全屏" : "全屏"}
            className="cursor-pointer text-white transition-colors duration-200 hover:text-primary-400"
          >
            <i className={`${isFullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"} text-xl`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}